import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useNFTVerification } from '@/hooks/useNFTVerification';
import { toastError } from '@/components/ui/use-toast';
import { API_BASE_URL } from '@/config';
import { authenticatedFetch, isAuthExpiredError } from '@/utils/authFetch';

interface TierStatus {
  tier_1?: boolean;
  tier_2?: boolean;
  tier_3?: boolean;
  profit_share_owed?: number;
}

interface NFTVerificationContextType {
  isVerifying: boolean;
  nftStatus: {
    owns_nft: boolean;
    is_expired: boolean;
  };
  tierStatus: TierStatus | null;
  refreshTierStatus: () => Promise<void>;
}

const NFTVerificationContext = createContext<NFTVerificationContextType | undefined>(undefined);

export const useNFTVerificationContext = () => {
  const context = useContext(NFTVerificationContext);
  if (context === undefined) {
    throw new Error('useNFTVerificationContext must be used within an NFTVerificationProvider');
  }
  return context;
};

interface NFTVerificationProviderProps {
  children: React.ReactNode;
}

export const NFTVerificationProvider: React.FC<NFTVerificationProviderProps> = ({ children }) => {
  const { connected } = useWallet();
  const [tierStatus, setTierStatus] = useState<TierStatus | null>(null);
  const [isDowngrading, setIsDowngrading] = useState(false);


  // Function to fetch tier status from API
  const fetchTierStatus = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/trading/tier/status`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (!response.ok) {
        if (response.status !== 401) {
          console.warn('[NFTVerificationContext] Tier status fetch failed:', response.status, response.statusText);
        }
        return null;
      }

      const data = await response.json();
      setTierStatus(data);
      console.log('[NFTVerificationContext] Tier status fetched successfully:', data);
      return data;
    } catch (error) {
      if (!isAuthExpiredError(error)) {
        console.error('[NFTVerificationContext] Unexpected error fetching tier status:', error);
      }
    }
    return null;
  }, []);

  // Function to downgrade Tier 3 user to Tier 1 when NFT is lost
  const downgradeTier3User = useCallback(async () => {
    if (isDowngrading) return; // Prevent multiple simultaneous downgrades
    
    setIsDowngrading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await authenticatedFetch(`${API_BASE_URL}/api/trading/tier/update`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tier_1: true, tier_2: false, tier_3: false })
      });

      if (response.ok) {
        const data = await response.json();
        setTierStatus(data);
        
        toastError({
          title: 'Tier Downgraded to Starter',
          description: 'You have been automatically downgraded to Tier 1 because you no longer own the required NFT from the Capitol Chilax collection for Tier 3 access.'
        });
      } else {
        throw new Error('Failed to downgrade tier');
      }
    } catch (error) {
      if (!isAuthExpiredError(error)) {
        console.error('Error downgrading tier:', error);
        toastError({
          title: 'Error',
          description: 'Failed to update tier status. Please refresh the page.'
        });
      }
    } finally {
      setIsDowngrading(false);
    }
  }, [isDowngrading]);

  // Create stable callback for NFT error handling
  const handleNFTError = useCallback((error: Error) => {
    console.error('NFT verification error:', error);

    // Only show error toast if:
    // 1. User is Tier 3
    // 2. Wallet is connected
    // 3. Haven't shown error before (avoid spam)
    // 4. This is a persistent verification failure (not just temporary network issues)
    // 5. The error has occurred multiple times (indicating a real problem)

    // For now, suppress error toasts since user is legitimately Tier 3
    // The NFT verification will still run for security but won't spam the user
    console.log('NFT verification failed, but user is verified as Tier 3 - monitoring silently');
  }, [tierStatus?.tier_3]);

  // NFT verification hook - ENABLED for continuous security monitoring
  const isCurrentlyTier3 = tierStatus?.tier_3 === true;
  const { nftStatus, isCheckingNFT } = useNFTVerification({
    enabled: isCurrentlyTier3 && connected, // Enable continuous verification for Tier 3 users with connected wallet
    onNFTLost: downgradeTier3User,
    onError: handleNFTError
  });



  // Fetch tier status on mount
  useEffect(() => {
    fetchTierStatus();
  }, [fetchTierStatus]);

  // Downgrade Tier 3 when wallet disconnects (server-side authority)
  useEffect(() => {
    const handleDisconnectDowngrade = async () => {
      try {
        if (tierStatus?.tier_3 && !connected && !isDowngrading) {
          setIsDowngrading(true);
          const token = localStorage.getItem('access_token');
          await authenticatedFetch(`${API_BASE_URL}/api/trading/wallet/disconnect`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          await fetchTierStatus();
        }
      } catch (e) {
        if (!isAuthExpiredError(e)) {
          console.warn('Wallet disconnect downgrade failed:', e);
        }
      } finally {
        setIsDowngrading(false);
      }
    };

    handleDisconnectDowngrade();
  }, [connected, tierStatus?.tier_3, isDowngrading, fetchTierStatus]);

  const value: NFTVerificationContextType = {
    isVerifying: isCheckingNFT || isDowngrading,
    nftStatus,
    tierStatus,
    refreshTierStatus: fetchTierStatus,
  };

  return (
    <NFTVerificationContext.Provider value={value}>
      {children}
    </NFTVerificationContext.Provider>
  );
};
